import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { fetchApi, api } from "../api/client";
import { Video, Download, Clock, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import LoadingSpinner from "../components/LoadingSpinner";
import ImageModal from "../components/ImageModal";

const VIDEO_STATUS = {
    none:        { label: "No Video",    color: "#888" },
    pending:     { label: "Pending",     color: "#facc15" },
    downloading: { label: "Downloading", color: "#3b82f6" },
    ready:       { label: "Ready",       color: "#22c55e" },
    failed:      { label: "Failed",      color: "#ef4444" },
};

function VideoPlayer({ src, onDownload, maxWidth = 800, autoPlay = false }) {
    const [error, setError] = React.useState(false);
    if (error) {
        return (
            <div style={{
                padding: 20, background: "var(--bg-secondary)", borderRadius: 8,
                border: "1px solid var(--border)", maxWidth, textAlign: "center",
            }}>
                <p style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 12 }}>
                    Video could not be played in the browser. Download to play locally.
                </p>
                {onDownload && (
                    <button
                        onClick={async (e) => {
                            e.stopPropagation();
                            try { await onDownload(); } catch (err) { alert("Download failed: " + err.message); }
                        }}
                        style={{
                            padding: "8px 20px", fontSize: 13, fontWeight: 600,
                            background: "var(--accent)", color: "#fff",
                            border: "none", borderRadius: 6, cursor: "pointer",
                        }}
                    >
                        Download Video
                    </button>
                )}
            </div>
        );
    }
    return (
        <video
            src={src}
            controls
            autoPlay={autoPlay}
            onError={() => setError(true)}
            onClick={e => e.stopPropagation()}
            style={{ width: "100%", maxWidth, borderRadius: 8 }}
        />
    );
}

export default function DipEvidence() {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVideo, setModalVideo] = useState(null);
    const [expandedSub, setExpandedSub] = useState(null);
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const params = search ? `?search=${encodeURIComponent(search)}` : "";
            const data = await fetchApi(`/dips/submit/recent${params}`);
            setSubmissions(data.submissions || []);
        } catch (e) {
            console.error("Failed to load dip submissions", e);
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) return <LoadingSpinner message="Loading dip submissions..." />;

    const thStyle = {
        padding: "8px 12px", fontSize: 11, fontWeight: 600, textTransform: "uppercase",
        letterSpacing: 0.5, color: "var(--text-secondary)", borderBottom: "1px solid var(--border)",
        textAlign: "left", whiteSpace: "nowrap",
    };
    const tdStyle = { padding: "8px 12px", fontSize: 13, borderBottom: "1px solid var(--border-light, rgba(255,255,255,0.05))" };

    return (
        <div>
            <div className="page-header">
                <h2>Dip Evidence</h2>
                <p style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                    Dip form submissions with video evidence
                </p>
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
                <input
                    type="text"
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") setSearch(searchInput); }}
                    placeholder="Filter by site ID or store name..."
                    className="filter-select"
                    style={{ flex: 1, maxWidth: 300 }}
                />
                <button
                    onClick={() => setSearch(searchInput)}
                    className="filter-select"
                    style={{ cursor: "pointer", fontWeight: 600 }}
                >
                    Filter
                </button>
                {search && (
                    <button
                        onClick={() => { setSearch(""); setSearchInput(""); }}
                        className="filter-select"
                        style={{ cursor: "pointer", color: "var(--text-secondary)" }}
                    >
                        Clear
                    </button>
                )}
                <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{submissions.length} submissions</span>
            </div>

            <div className="card" style={{ overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Site</th>
                            <th style={thStyle}>Store</th>
                            <th style={thStyle}>Submitted</th>
                            <th style={thStyle}>By</th>
                            <th style={thStyle}>Tanks</th>
                            <th style={thStyle}>Issues</th>
                            <th style={thStyle}>Video</th>
                        </tr>
                    </thead>
                    <tbody>
                        {submissions.length === 0 && (
                            <tr><td colSpan={7} style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)", padding: 32 }}>
                                No dip submissions yet
                            </td></tr>
                        )}
                        {submissions.map(sub => {
                            const vCfg = VIDEO_STATUS[sub.video_status] || VIDEO_STATUS.none;
                            const isExpanded = expandedSub === sub.id;
                            const hasIssues = sub.issue_count > 0;
                            return (
                                <React.Fragment key={sub.id}>
                                    <tr
                                        style={{ cursor: "pointer" }}
                                        onClick={() => setExpandedSub(isExpanded ? null : sub.id)}
                                    >
                                        <td style={tdStyle}>
                                            <Link to={`/store/${sub.site_id}`} style={{ color: "var(--accent)", fontWeight: 600 }}
                                                onClick={e => e.stopPropagation()}>
                                                {sub.site_id}
                                            </Link>
                                        </td>
                                        <td style={tdStyle}>{sub.store_name || "--"}</td>
                                        <td style={tdStyle}>{new Date(sub.submitted_at).toLocaleString()}</td>
                                        <td style={tdStyle}>{sub.submitted_by || "--"}</td>
                                        <td style={tdStyle}>{sub.tank_count}</td>
                                        <td style={tdStyle}>
                                            {hasIssues ? (
                                                <span style={{ color: "#ef4444", fontWeight: 600 }}>{sub.issue_count}</span>
                                            ) : (
                                                <span style={{ color: "#22c55e" }}>0</span>
                                            )}
                                        </td>
                                        <td style={tdStyle}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                {sub.video_status === "ready" && sub.video_url ? (
                                                    <>
                                                    <button
                                                        className="preset-action-btn"
                                                        onClick={e => { e.stopPropagation(); setModalVideo({ src: sub.video_url, subId: sub.id }); }}
                                                        title="View video"
                                                        style={{ padding: "3px 8px" }}
                                                    >
                                                        <Video size={14} /> Watch
                                                    </button>
                                                    <button
                                                        className="preset-action-btn"
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            try {
                                                                const result = await api.dips.submissionVideoDownload(sub.id);
                                                                if (result.url) window.location.href = result.url;
                                                            } catch (err) {
                                                                alert("Download failed: " + err.message);
                                                            }
                                                        }}
                                                        title="Download video"
                                                        style={{ padding: "3px 8px" }}
                                                    >
                                                        <Download size={14} />
                                                    </button>
                                                    </>
                                                ) : (
                                                    <span style={{ fontSize: 11, color: vCfg.color, display: "flex", alignItems: "center", gap: 3 }}>
                                                        {(sub.video_status === "pending" || sub.video_status === "downloading") && <Clock size={12} />}
                                                        {sub.video_status === "failed" && <AlertTriangle size={12} />}
                                                        {vCfg.label}
                                                    </span>
                                                )}
                                                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                            </div>
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <tr>
                                            <td colSpan={7} style={{ padding: "0 12px 12px", background: "var(--bg-card)" }}>
                                                <SubmissionDetail subId={sub.id} />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Video lightbox */}
            {modalVideo && (
                <div className="image-modal-overlay" onClick={() => setModalVideo(null)}>
                    <div className="image-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 900 }}>
                        <button className="image-modal-close" onClick={() => setModalVideo(null)}>ESC</button>
                        <VideoPlayer
                            src={modalVideo.src}
                            autoPlay
                            maxWidth={900}
                            onDownload={modalVideo.subId ? async () => {
                                const result = await api.dips.submissionVideoDownload(modalVideo.subId);
                                if (result.url) window.location.href = result.url;
                            } : null}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

function SubmissionDetail({ subId }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modalImages, setModalImages] = useState(null);
    const [modalIndex, setModalIndex] = useState(0);

    useEffect(() => {
        fetchApi(`/dips/submit/${subId}`)
            .then(d => setDetail(d))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [subId]);

    if (loading) return <div style={{ padding: 12, color: "var(--text-secondary)", fontSize: 12 }}>Loading...</div>;
    if (!detail) return null;

    const tanks = detail.tanks || [];
    const formPhotos = detail.formPhotos || [];
    const stills = detail.stills || [];

    // Group form photos by tank
    const photosByTank = {};
    formPhotos.forEach(p => {
        const key = p.tankNumber ? `tank_${p.tankNumber}` : "other";
        if (!photosByTank[key]) photosByTank[key] = { tankNumber: p.tankNumber, product: p.product, photos: [] };
        photosByTank[key].photos.push(p);
    });

    return (
        <div style={{ padding: "8px 0" }}>
            {detail.dip_time && (
                <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8 }}>
                    Dip time: {new Date(detail.dip_time).toLocaleString()}
                </div>
            )}

            {/* Tank data */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                {tanks.map(t => {
                    const hasIssue = t.fill_point_closed === false || t.seals_ok === false
                        || t.dipstick_seal_ok === false || t.water_paste_result === "water";
                    return (
                        <div key={t.id} style={{
                            background: "var(--bg-main)", borderRadius: 8, padding: "10px 12px",
                            border: `1px solid ${hasIssue ? "#ef4444" : "var(--border)"}`,
                            minWidth: 180, flex: "1 1 200px",
                        }}>
                            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
                                Tank {t.tank_number} — {t.product || "?"}
                            </div>
                            {t.dip_measure_litres != null && (
                                <div style={{ fontSize: 12, marginBottom: 4 }}>Volume: {Number(t.dip_measure_litres).toLocaleString()}L</div>
                            )}
                            <div style={{ fontSize: 12, color: t.water_paste_result === "water" ? "#ef4444" : t.water_paste_result === "na" ? "var(--text-secondary)" : "#22c55e" }}>
                                Paste test: {t.water_paste_result === "water" ? "WATER DETECTED" : t.water_paste_result === "clear" ? "Clear" : t.water_paste_result === "na" ? "N/A" : t.water_paste_result || "--"}
                            </div>
                            <div style={{ fontSize: 12, color: t.fill_point_closed === false ? "#ef4444" : t.fill_point_closed === true ? "#22c55e" : "var(--text-secondary)" }}>
                                Fill point closed: {t.fill_point_closed === true ? "Yes" : t.fill_point_closed === false ? "No" : "--"}
                            </div>
                            <div style={{ fontSize: 12, color: t.seals_ok === false ? "#ef4444" : t.seals_ok === true ? "#22c55e" : "var(--text-secondary)" }}>
                                Seals: {t.seals_ok === true ? "OK" : t.seals_ok === false ? "Damaged" : "--"}
                            </div>
                            <div style={{ fontSize: 12, color: t.dipstick_seal_ok === false ? "#ef4444" : t.dipstick_seal_ok === true ? "#22c55e" : "var(--text-secondary)" }}>
                                Dipstick rubber: {t.dipstick_seal_ok === true ? "OK" : t.dipstick_seal_ok === false ? "Damaged" : "--"}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Form photos grouped by tank */}
            {formPhotos.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>
                        Form Photos ({formPhotos.length})
                    </div>
                    {Object.values(photosByTank).map(group => (
                        <div key={group.tankNumber || "other"} style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 }}>
                                {group.tankNumber ? `Tank ${group.tankNumber}` : "Photos"}
                                {group.product && ` — ${group.product}`}
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {group.photos.map((p, i) => {
                                    const allPhotos = formPhotos.map(fp => ({ url: fp.url, label: fp.tankNumber ? `Tank ${fp.tankNumber}` : "Photo" }));
                                    const globalIdx = formPhotos.indexOf(p);
                                    return (
                                        <img
                                            key={i}
                                            src={p.url}
                                            alt={`Tank ${group.tankNumber || "?"} photo`}
                                            onClick={(e) => { e.stopPropagation(); setModalImages(allPhotos); setModalIndex(globalIdx >= 0 ? globalIdx : 0); }}
                                            style={{
                                                width: 120, height: 90, objectFit: "cover",
                                                borderRadius: 4, cursor: "pointer",
                                                border: "1px solid var(--border)",
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Video */}
            {detail.videoUrl && (
                <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>
                        Video Evidence
                        <button
                            onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                    const result = await api.dips.submissionVideoDownload(subId);
                                    if (result.url) window.location.href = result.url;
                                } catch (err) {
                                    alert("Download failed: " + err.message);
                                }
                            }}
                            style={{
                                marginLeft: 8, fontSize: 11, fontWeight: 500,
                                color: "var(--accent)", background: "none", border: "none",
                                cursor: "pointer", textDecoration: "underline",
                            }}
                        >
                            Download
                        </button>
                    </div>
                    <VideoPlayer
                        src={detail.videoUrl}
                        maxWidth={600}
                        onDownload={async () => {
                            const result = await api.dips.submissionVideoDownload(subId);
                            if (result.url) window.location.href = result.url;
                        }}
                    />
                </div>
            )}

            {/* Stills */}
            {stills.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>
                        Video Stills ({stills.length})
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {stills.map((s, i) => (
                            <div key={i} style={{ position: "relative" }}>
                                <img
                                    src={s.url}
                                    alt={`Still ${i + 1}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setModalImages(stills.map((st, si) => ({
                                            url: st.url,
                                            label: `${Math.floor(si * 30 / 60)}:${String(si * 30 % 60).padStart(2, "0")}`,
                                        })));
                                        setModalIndex(i);
                                    }}
                                    style={{
                                        width: 140, height: 100, objectFit: "cover",
                                        borderRadius: 4, cursor: "pointer",
                                        border: "1px solid var(--border)",
                                    }}
                                />
                                <span style={{
                                    position: "absolute", bottom: 2, left: 2,
                                    fontSize: 9, fontWeight: 600, padding: "1px 4px",
                                    background: "rgba(0,0,0,0.6)", color: "#fff", borderRadius: 2,
                                }}>
                                    {Math.floor(i * 30 / 60)}:{String(i * 30 % 60).padStart(2, "0")}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Image lightbox with zoom */}
            {modalImages && (
                <ImageModal
                    images={modalImages}
                    currentIndex={modalIndex}
                    onClose={() => setModalImages(null)}
                    onPrev={modalIndex > 0 ? () => setModalIndex(modalIndex - 1) : null}
                    onNext={modalIndex < modalImages.length - 1 ? () => setModalIndex(modalIndex + 1) : null}
                />
            )}
        </div>
    );
}
